import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../../../stores/hooks/hooks.ts";
import { useEffect, useMemo, useState } from "react";
import {
  RevButton,
  RevCheckbox,
  RevDialog,
  RevDialogFooter,
  RevPersonaCard,
  RevRadioGroup,
} from "@revmatics/catalyst";
import { GenerateAIPersonaData } from "../../../../../../../../stores/types/personasTypes.tsx";
import { generateAIPersonaThunk } from "../../../../../../../../stores/thunks/personasThunk.tsx";
import { setIsLoadingGenerate } from "../../../../../../../../stores/slice/personasSlice.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLoader } from "@fortawesome/pro-light-svg-icons";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  handleGenerateLoading: (numGenerate: number) => void;
};

type DialogPersona = {
  id: number;
  isSelected: boolean;
};

const radioOptions = [
  { value: 1, label: "1 persona" },
  { value: 3, label: "3 personas" },
  { value: 5, label: "5 personas" },
];

export const GeneratePersonaDialog: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  handleGenerateLoading,
}) => {
  const dispatch = useAppDispatch();
  const personasDataFromStore = useAppSelector(
    (state) => state.personas.personas,
  );
  const isLoadingGenerate = useAppSelector(
    (state) => state.personas.isLoadingGenerate,
  );
  const personasData = useMemo(
    () => personasDataFromStore ?? [],
    [personasDataFromStore],
  );
  const [dialogPersonasList, setDialogPersonaList] = useState<DialogPersona[]>(
    [],
  );
  const [numPersonas, setNumPersonas] = useState<number>(3);
  const [isLoading, setIsLoading] = useState(false);
  const selectedPersonaIds = useMemo(() => {
    return dialogPersonasList.filter((p) => p.isSelected).map((p) => p.id);
  }, [dialogPersonasList]);

  useEffect(() => {
    const allPersonas =
      personasData.map((persona) => ({
        id: persona.id,
        isSelected: true,
      })) ?? [];
    setDialogPersonaList(allPersonas);
  }, [personasData]);

  const onSelect = (personaId: number) => {
    setDialogPersonaList((prevSelected) =>
      prevSelected.map((p) =>
        p.id === personaId ? { ...p, isSelected: !p.isSelected } : p,
      ),
    );
  };

  const onToggleSelectAll = () => {
    const allSelected = selectedPersonaIds.length === dialogPersonasList.length;
    setDialogPersonaList((prevSelected) =>
      prevSelected.map((p) => ({ ...p, isSelected: !allSelected })),
    );
  };

  const handleNumPersonaChange = (value: number | string) => {
    const numberValue = Number(value);
    setNumPersonas(numberValue);
  };

  const handleSubmit = () => {
    setIsLoading(true);
    const requestData: GenerateAIPersonaData = {
      num_personas: numPersonas,
      persona_ids: selectedPersonaIds,
    };
    dispatch(generateAIPersonaThunk(requestData))
      .unwrap()
      .then(() => {
        handleGenerateLoading(numPersonas + dialogPersonasList.length);
      })
      .catch((error) => {
        dispatch(setIsLoadingGenerate(false));
        setIsOpen(false);
        console.error("Failed to generate AI personas:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const dialogActions: React.ReactElement[] = [
    <RevButton
      key="cancel"
      text="Cancel"
      variant="secondary"
      handleClick={() => setIsOpen(false)}
    />,
    <RevButton
      key="generate"
      text="Generate"
      handleClick={handleSubmit}
      isLoading={isLoading}
      disabled={
        dialogPersonasList.length + numPersonas > 30 ||
        selectedPersonaIds.length === 0
      }
    />,
  ];

  const loadingDialogActions: React.ReactElement[] = [
    <RevButton key="close" text="Close" handleClick={() => setIsOpen(false)} />,
  ];

  const generateFooter: React.ReactElement = (
    <RevDialogFooter list={loadingDialogActions} variant={"center"} />
  );

  if (!isLoadingGenerate) {
    return (
      <RevDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        mobileSize="xs"
        title={"Generate AI Personas"}
        btnList={dialogActions}
      >
        <div className="flex flex-col gap-6 max-h-[calc(100%-150px)] md:max-h-fit">
          <div className="flex flex-col gap-2 h-full">
            <div className="flex items-center">
              <div>
                <p className="typography-body-bold-sm">Select Input Personas</p>
                <p className="typography-body-xs text-monochrome-600">
                  Select personas to use as inputs to generate new AI personas.
                  It is generally recommended to use all valid personas.
                </p>
              </div>
              <RevCheckbox
                label={`Select All (${dialogPersonasList.length})`}
                full={true}
                checked={selectedPersonaIds.length > 0}
                checkedFull={
                  selectedPersonaIds.length === dialogPersonasList.length
                }
                handleChecked={onToggleSelectAll}
                className="flex whitespace-nowrap h-fit"
              />
            </div>
            <div className="flex flex-col gap-2 p-2 lg:h-[289px] border-monochrome-200 bg-monochrome-50 border rounded-lg overflow-y-auto scroll-vertical">
              {personasData?.map((persona) => {
                return (
                  <RevPersonaCard
                    key={persona.id}
                    persona={persona}
                    isButtonVariant
                    showCheckbox
                    className="max-h-[80px] mt-3"
                    selected={selectedPersonaIds.some((p) => p === persona.id)}
                    onClick={onSelect}
                  />
                );
              })}
            </div>
          </div>
          <div>
            <RevRadioGroup
              label="Number of Personas to Generate"
              isLabelBold={true}
              name="num_personas"
              options={radioOptions}
              displayDirection="horizontal"
              value={numPersonas}
              onChange={handleNumPersonaChange}
            />
          </div>
        </div>
      </RevDialog>
    );
  } else {
    return (
      <RevDialog
        isOpen={isOpen && isLoadingGenerate}
        setIsOpen={setIsOpen}
        title={""}
        mobileSize="xs"
        btnList={loadingDialogActions}
        footerComponent={generateFooter}
        className={"!py-2"}
      >
        <div className="flex flex-col gap-4 w-full items-center">
          <div className="flex flex-col gap-2 items-center">
            <FontAwesomeIcon
              icon={faLoader}
              className="text-primary-500 text-[40px]"
            />
            <h5>Generating...</h5>
          </div>
          <p className="text-monochrome-600 typography-body-sm">
            This may take a minute... but that's what it takes to get it right.
          </p>
          <p className="text-center py-3 bg-primary-25 rounded-lg">
            We're aligning the stars just for you. Generating AI persona(s) in
            the background...
          </p>
          <p className="text-monochrome-600 typography-body-sm">
            Feel free to close this dialog.
          </p>
        </div>
      </RevDialog>
    );
  }
};
